import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const sb: any = supabase;

export interface PayoutRow {
  id: string;
  expert_user_id: string;
  expert_name: string | null;
  expert_avatar: string | null;
  period_month: string;
  hourly_rate: number;
  total_hours: number;
  total_amount: number;
  currency: string;
  status: 'draft' | 'closed' | 'paid' | 'cancelled';
  notes: string | null;
  created_at: string;
  paid_at: string | null;
}

export function useExpertPayouts() {
  return useQuery({
    queryKey: ['expert-payouts'],
    queryFn: async (): Promise<PayoutRow[]> => {
      const { data, error } = await sb
        .from('expert_payouts')
        .select('*, profiles:profiles!expert_payouts_expert_user_id_fkey(id, full_name, avatar_url)')
        .neq('status', 'cancelled')
        .order('period_month', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        // Fallback without join if FK not auto-detected
        const { data: d2, error: e2 } = await sb.from('expert_payouts').select('*').neq('status', 'cancelled').order('period_month', { ascending: false });
        if (e2) throw e2;
        const expertIds = Array.from(new Set((d2 ?? []).map((r: any) => r.expert_user_id)));
        const profMap: Record<string, any> = {};
        if (expertIds.length) {
          const { data: profs } = await sb.from('profiles').select('id, full_name, avatar_url').in('id', expertIds);
          (profs ?? []).forEach((p: any) => { profMap[p.id] = p; });
        }
        return (d2 ?? []).map((r: any) => ({
          ...r,
          expert_name: profMap[r.expert_user_id]?.full_name ?? null,
          expert_avatar: profMap[r.expert_user_id]?.avatar_url ?? null,
        }));
      }
      return (data ?? []).map((r: any) => ({
        ...r,
        expert_name: r.profiles?.full_name ?? null,
        expert_avatar: r.profiles?.avatar_url ?? null,
      }));
    },
  });
}

export function usePayoutDetail(payoutId: string | undefined) {
  return useQuery({
    queryKey: ['expert-payout', payoutId],
    enabled: !!payoutId,
    queryFn: async () => {
      const { data: payout, error } = await sb
        .from('expert_payouts')
        .select('*')
        .eq('id', payoutId)
        .maybeSingle();
      if (error) throw error;
      if (!payout) return null;

      const { data: items } = await sb
        .from('expert_payout_items')
        .select('*')
        .eq('payout_id', payoutId)
        .order('session_date', { ascending: true });

      const { data: profile } = await sb
        .from('profiles')
        .select('id, full_name, avatar_url, email')
        .eq('id', payout.expert_user_id)
        .maybeSingle();

      // Enrich items with organization name from cohorts
      const cohortIds = Array.from(new Set((items ?? []).map((it: any) => it.cohort_id).filter(Boolean)));
      const orgByCohort: Record<string, { id: string; name: string } | null> = {};
      if (cohortIds.length) {
        const { data: cohorts } = await sb
          .from('cohorts')
          .select('id, organization_id, organizations(id, name)')
          .in('id', cohortIds);
        (cohorts ?? []).forEach((c: any) => {
          orgByCohort[c.id] = c.organizations ?? null;
        });
      }
      const enrichedItems = (items ?? []).map((it: any) => ({
        ...it,
        organization_id: it.cohort_id ? orgByCohort[it.cohort_id]?.id ?? null : null,
        organization_name: it.cohort_id ? orgByCohort[it.cohort_id]?.name ?? null : null,
      }));

      return { payout, items: enrichedItems, expert: profile };
    },
  });
}

export function useExpertMonthSessions(expertId: string | null, monthStart: string | null) {
  return useQuery({
    queryKey: ['expert-month-sessions', expertId, monthStart],
    enabled: !!expertId && !!monthStart,
    queryFn: async () => {
      const { data, error } = await sb.rpc('get_expert_sessions_for_month', {
        _expert_id: expertId,
        _month: monthStart,
      });
      if (error) throw error;
      return (data ?? []) as Array<{
        schedule_item_id: string;
        session_date: string;
        session_type: string;
        title: string;
        cohort_id: string;
        cohort_name: string;
        duration_hours: number;
        recording_link: string | null;
        has_recording: boolean;
      }>;
    },
  });
}

export function useCreatePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { expertId: string; month: string; hourlyRate: number; currency?: string; notes?: string }) => {
      const { data, error } = await sb.rpc('create_expert_payout', {
        _expert_id: args.expertId,
        _month: args.month,
        _hourly_rate: args.hourlyRate,
        _currency: args.currency ?? 'COP',
        _notes: args.notes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expert-payouts'] });
      qc.invalidateQueries({ queryKey: ['live-sessions-audit'] });
      toast({ title: 'Liquidación creada', description: 'Se generó la liquidación correctamente.' });
    },
    onError: (e: any) => {
      const msg = e?.message?.includes('duplicate') || e?.code === '23505'
        ? 'Ya existe una liquidación activa para este experto en ese mes.'
        : (e?.message ?? 'Error al crear la liquidación');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    },
  });
}

export function useUpdatePayoutStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: 'draft' | 'closed' | 'paid' | 'cancelled' }) => {
      const patch: any = { status: args.status };
      if (args.status === 'paid') { patch.paid_at = new Date().toISOString(); }
      const { error } = await sb.from('expert_payouts').update(patch).eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['expert-payouts'] });
      qc.invalidateQueries({ queryKey: ['expert-payout', vars.id] });
      toast({ title: 'Liquidación actualizada' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e?.message, variant: 'destructive' }),
  });
}

export function useReopenPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await sb.rpc('reopen_expert_payout', { _payout_id: id });
      if (error) throw error;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['expert-payouts'] });
      qc.invalidateQueries({ queryKey: ['expert-payout', id] });
      toast({ title: 'Liquidación reabierta y recalculada' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e?.message, variant: 'destructive' }),
  });
}

export function useDeletePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await sb.from('expert_payouts').update({ status: 'cancelled' }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expert-payouts'] });
      toast({ title: 'Liquidación cancelada' });
    },
  });
}
